import { LoadingButton } from '@mui/lab'
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React from 'react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AnimalDto, AnimalShareAnnounceDto } from '../../../interactors/gen/backendClient'
import { useEditAnimalMutation } from '../../../store/useEditAnimalMutation'
import { surfaceCardSx } from '../../common/surfaceCardSx'
import { colors, theme } from '../../theme'
import { ExpandLess, ExpandMore, InfoOutlined, OpenInNew, Save } from '@mui/icons-material'

interface ShareAnimalFormProps {
  animal: AnimalDto
}

export const ShareAnimalForm: React.FC<ShareAnimalFormProps> = ({ animal }) => {
  const isDesktop = useMediaQuery('(min-width:1100px)')
  const [isEditingDiffusion, setIsEditingDiffusion] = useState(isDesktop)
  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = useForm<AnimalShareAnnounceDto>({
    defaultValues: {
      petso: animal.shareAnnounce?.petso ?? true,
      wamiz: animal.shareAnnounce?.wamiz ?? true,
    },
  })

  const editAnimalMutation = useEditAnimalMutation({})
  const isAnyDiffusionEnabled = watch('petso') || watch('wamiz')

  const isAdoptable =
    animal.adoption.status === 'adoptable' || animal.adoption.status === 'in-the-process-of-being-adoptable'

  const disabledTooltip = isAdoptable
    ? ''
    : "L'animal doit être en statut « adoptable » ou « en cours d'adoption » pour diffuser son annonce."

  const onSubmit = (data: AnimalShareAnnounceDto) => {
    editAnimalMutation.mutate(
      { ...animal, shareAnnounce: data },
      {
        onSuccess: () => reset(data),
      }
    )
  }

  const updateButtonSx = {
    textTransform: 'none',
    borderRadius: '999px',
    minWidth: 0,
    flexShrink: 0,
    backgroundColor: '#FFFFFF',
    borderColor: 'rgba(105, 141, 172, 0.42)',
    color: '#203864',
    '& .MuiLoadingButton-loadingIndicator': {
      color: '#203864',
    },
    '&:hover': {
      backgroundColor: 'rgba(105, 141, 172, 0.06)',
      borderColor: 'rgba(105, 141, 172, 0.62)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.78)',
      borderColor: 'rgba(122, 129, 151, 0.2)',
      color: 'rgba(122, 129, 151, 0.65)',
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      color: 'rgba(122, 129, 151, 0.55)',
    },
  } as const

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        px: { xs: 1.5, md: 1.75 },
        pt: { xs: 1.35, md: 1.5 },
        pb: { xs: 1.35, sm: 1.35, md: 0.75 },
        width: '100%',
        height: '100%',
        minWidth: 0,
        maxWidth: '100%',
        ...surfaceCardSx,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '999px',
              flexShrink: 0,
              backgroundColor: isAnyDiffusionEnabled ? '#698DAC' : 'rgba(122, 129, 151, 0.28)',
              border: '1px solid rgba(122, 129, 151, 0.18)',
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: 13, color: colors.cello, lineHeight: 1.2 }}
            noWrap
            title="Diffusion"
          >
            Diffusion
          </Typography>
          <Tooltip
            title="L'annonce sera diffusée si le statut de l'animal est « adoptable » et que son profil est suffisamment complété."
            arrow
          >
            <InfoOutlined sx={{ width: 15, height: 15, color: '#7A8197', flexShrink: 0 }} />
          </Tooltip>
        </Box>

        {isDesktop && (
          <LoadingButton
            type="submit"
            variant="outlined"
            color="primary"
            disabled={!isAdoptable || editAnimalMutation.isLoading || !isDirty}
            loading={editAnimalMutation.isLoading}
            startIcon={<Save />}
            sx={{
              minHeight: 30,
              px: 1.15,
              fontSize: 12,
              fontWeight: 700,
              alignSelf: 'center',
              ...updateButtonSx,
            }}
          >
            Mettre à jour
          </LoadingButton>
        )}

        {!isDesktop && (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setIsEditingDiffusion((prev) => !prev)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setIsEditingDiffusion((prev) => !prev)
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              cursor: isAdoptable ? 'pointer' : 'not-allowed',
              opacity: isAdoptable ? 1 : 0.6,
              color: colors.cello,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
              Modifier
            </Typography>
            <Box sx={{ ml: -0.25, display: 'flex', alignItems: 'center' }}>
              {isEditingDiffusion ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          </Box>
        )}
      </Box>

      <Tooltip title={disabledTooltip} arrow disableHoverListener={isAdoptable}>
        <Box sx={{ mt: 0.75, flex: '1 1 auto' }}>
          <Collapse in={isDesktop || isEditingDiffusion} timeout="auto" unmountOnExit={!isDesktop}>
            <Box sx={{ mt: 0.25 }}>
              <FormControl component="fieldset" fullWidth>
                <FormGroup
                  sx={{
                    gap: 0.1,
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    alignItems: 'start',
                  }}
                >
                  <Controller
                    name="petso"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        disabled={!isAdoptable}
                        sx={{
                          m: 0,
                          minHeight: 40,
                          alignItems: 'center',
                          '& .MuiFormControlLabel-label': {
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        control={
                          <Switch
                            checked={value}
                            onChange={(_, checked) => onChange(checked)}
                            color="primary"
                            disabled={!isAdoptable}
                            size="medium"
                            sx={{
                              mr: 0.5,
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#FFFFFF',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'rgba(105, 141, 172, 0.44)',
                                opacity: 1,
                              },
                              '& .MuiSwitch-thumb': {
                                backgroundColor: value ? '#698DAC' : '#D8DDE8',
                              },
                              '& .MuiSwitch-track': {
                                borderRadius: 999,
                                backgroundColor: 'rgba(105, 141, 172, 0.2)',
                                opacity: 1,
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, py: 0.15 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontSize: 12.5, color: colors.cello }}
                              noWrap
                            >
                              Partager sur
                            </Typography>
                            <img
                              src={'https://www.petso.fr/_next/static/media/logo.19b70203.svg'}
                              alt="Petso Logo"
                              style={{ height: 22, marginLeft: 4 }}
                            />
                            <Typography
                              variant="body2"
                              fontFamily="insaniburger"
                              sx={{ fontSize: 14 }}
                              color={theme.palette.primary.main}
                            >
                              Petso
                            </Typography>
                            <Tooltip title="Voir la page publique de l'animal" arrow>
                              <span>
                                <IconButton
                                  component="a"
                                  href={`https://petso.fr/annonces/${animal.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  disabled={!isAdoptable || !value}
                                  sx={{
                                    ml: 0.25,
                                    color: '#203864',
                                    backgroundColor: 'rgba(32, 56, 100, 0.08)',
                                    '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
                                  }}
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                      />
                    )}
                  />

                  <Controller
                    name="wamiz"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        disabled={!isAdoptable}
                        sx={{
                          m: 0,
                          minHeight: 40,
                          alignItems: 'center',
                          '& .MuiFormControlLabel-label': {
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        control={
                          <Switch
                            checked={value}
                            onChange={(_, checked) => onChange(checked)}
                            color="primary"
                            disabled={!isAdoptable}
                            size="medium"
                            sx={{
                              mr: 0.5,
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#FFFFFF',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'rgba(105, 141, 172, 0.44)',
                                opacity: 1,
                              },
                              '& .MuiSwitch-thumb': {
                                backgroundColor: value ? '#698DAC' : '#D8DDE8',
                              },
                              '& .MuiSwitch-track': {
                                borderRadius: 999,
                                backgroundColor: 'rgba(105, 141, 172, 0.2)',
                                opacity: 1,
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, py: 0.15 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontSize: 12.5, color: colors.cello }}
                              noWrap
                            >
                              Partager sur
                            </Typography>
                            <img
                              src="https://wamiz.com/build/images/newsletter/wamiz-logo.png"
                              alt="Wamiz Logo"
                              style={{ height: 17, marginLeft: 8 }}
                            />
                          </Box>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </FormControl>

              {!isDesktop && (
                <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton
                    type="submit"
                    variant="outlined"
                    color="primary"
                    disabled={!isAdoptable || editAnimalMutation.isLoading || !isDirty}
                    loading={editAnimalMutation.isLoading}
                    startIcon={<Save />}
                    sx={{
                      minHeight: 32,
                      px: 1.4,
                      fontSize: 12.5,
                      fontWeight: 700,
                      ...updateButtonSx,
                    }}
                  >
                    Mettre à jour
                  </LoadingButton>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Tooltip>
    </Box>
  )
}
